import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Paginated, SignatureDocument, SignatureStatus } from '@worki/shared';
import { api, buildQuery } from '../../lib/api.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { usePageSearch } from '../../lib/pageSearch.js';
import { Button } from '../../components/ui.js';
import { Icon } from '../../components/Icon.js';
import { ConfirmDialog } from '../../components/Modal.js';
import { DocumentList } from './List.js';
import { useDocumentActions } from './useDocumentActions.js';
import { DocumentDetail } from './Detail.js';

/**
 * SIG — documents sent out to be signed.
 *
 * Its own screen rather than a tab of quotes, because what gets signed here is
 * usually not priced work: a safety briefing before a crew starts, a form the
 * customer signs on the doorstep, terms accepted before a job.
 *
 * SIG-004 moved making one onto a screen of its own. This one lists what has
 * been sent and what came back; creating is a route, not a modal, because the
 * pages of a document need the tall side of a phone and a modal gives them the
 * short side minus two bands of chrome.
 */
export function SigningWorkspace() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SignatureStatus | ''>('');
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<SignatureDocument | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SignatureDocument | null>(null);
  const [params, setParams] = useSearchParams();

  /*
   * `?open=<id>` opens one document straight away, so a row found in the send
   * history leads to the same panel as a row found here. Fetched by id rather
   * than looked for in the list: the document being asked for is frequently not
   * on the page the list happens to be showing.
   */
  const openId = params.get('open');
  const requested = useQuery({
    queryKey: ['signatures', 'one', openId],
    queryFn: () => api.get<SignatureDocument>(`/signatures/${openId}`),
    enabled: Boolean(openId),
  });
  useEffect(() => {
    if (!requested.data) return;
    setActive(requested.data);
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [requested.data]);

  const debounced = useDebounced(search, 300);
  /* UI-11 — the box the list drew is in the bar now, beside the screen name. */
  usePageSearch(search, setSearch, t('signing.searchPlaceholder'));
  useEffect(() => setPage(1), [debounced, status]);

  const list = useQuery({
    queryKey: ['signatures', { debounced, status, page }],
    queryFn: () =>
      api.get<Paginated<SignatureDocument>>(`/signatures${buildQuery({ q: debounced, status, page, pageSize: 25 })}`),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['signatures'] });

  // Chasing and deleting are shared with the send history, which lists the same
  // documents: one definition, so "send again" means one thing in both places.
  const { resend, remove, copyDialog } = useDocumentActions(invalidate);

  return (
    <>
      <div className="page-header">
        <p className="muted">{t('signing.subtitle')}</p>
        <Button variant="primary" icon={<Icon name="plus" size={17} />} onClick={() => navigate('/signing/new')}>
          {t('signing.new')}
        </Button>
      </div>

      <DocumentList
        rows={list.data?.items ?? []}
        loading={list.isLoading}
        status={status}
        onStatusChange={setStatus}
        page={page}
        pageSize={list.data?.pageSize ?? 25}
        total={list.data?.total ?? 0}
        onPageChange={setPage}
        onOpen={setActive}
        onResend={(document) => resend.mutate(document)}
        onDelete={setConfirmDelete}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('signing.deleteTitle')}
        message={t('signing.deleteBody')}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() =>
          confirmDelete && remove.mutate(confirmDelete, { onSettled: () => setConfirmDelete(null) })
        }
      />

      {/* 1.17 — "send a copy" on a signed document's row. */}
      {copyDialog}

      {active && (
        <DocumentDetail
          /* Keyed by id so that opening a second document builds a fresh panel
             rather than carrying the first one's placements into it. */
          key={active.id}
          document={active}
          agentReview={Boolean(params.get('agentRun'))}
          onClose={() => { setActive(null); const run = params.get('agentRun'); if (run) { void queryClient.invalidateQueries({ queryKey: ['agent'] }); navigate('/agent?run=' + encodeURIComponent(run)); } }}
          onChanged={invalidate}
        />
      )}
    </>
  );
}
